import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { selectToken } from '@store/slices/authSlice'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment
} from '@mui/material'
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CameraAlt as CameraIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'

const Profile = () => {
  const { t } = useTranslation()
  const token = useSelector(selectToken)
  
  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  
  const [profile, setProfile] = useState({
    id: '',
    name: '',
    email: '',
    role: '',
    createdAt: '',
    updatedAt: ''
  })

  const [tempProfile, setTempProfile] = useState({ ...profile })
  
  // Password change states
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  })
  const [changingPassword, setChangingPassword] = useState(false)

  // Fetch admin profile on component mount
  useEffect(() => {
    if (token) {
      fetchProfile()
    } else {
      setError('Authentication token not found')
      setLoading(false)
    }
  }, [token])

  const fetchProfile = async () => {
    try {
      setLoading(true)
      setError(null)

      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api'
      const response = await fetch(`${apiUrl}/admin/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch profile: ${response.status}`)
      }

      const result = await response.json()
      
      if (result.success) {
        setProfile(result.profile)
        setTempProfile(result.profile)
      } else {
        throw new Error(result.message || 'Failed to fetch profile')
      }
    } catch (error) {
      console.error('❌ Failed to fetch profile:', error)
      setError(error.message || 'Failed to fetch profile')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = () => {
    setTempProfile({ ...profile })
    setIsEditing(true)
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api'
      const response = await fetch(`${apiUrl}/admin/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: tempProfile.name,
          email: tempProfile.email
        })
      })

      if (!response.ok) {
        throw new Error(`Failed to update profile: ${response.status}`)
      }

      const result = await response.json()
      
      if (result.success) {
        setProfile(result.profile)
        setTempProfile(result.profile)
        setIsEditing(false)
        console.log('✅ Profile updated successfully')
      } else {
        throw new Error(result.message || 'Failed to update profile')
      }
    } catch (error) {
      console.error('❌ Failed to update profile:', error)
      alert('Failed to update profile. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setTempProfile({ ...profile })
    setIsEditing(false)
  }

  const handleInputChange = (field, value) => {
    setTempProfile(prev => ({ ...prev, [field]: value }))
  }

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New passwords do not match')
      return
    }

    if (passwordData.newPassword.length < 6) {
      alert('Password must be at least 6 characters long')
      return
    }

    try {
      setChangingPassword(true)
      
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api'
      const response = await fetch(`${apiUrl}/admin/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      })

      if (!response.ok) {
        throw new Error(`Failed to change password: ${response.status}`)
      }

      const result = await response.json()
      
      if (result.success) {
        setShowPasswordForm(false)
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        })
        console.log('✅ Password changed successfully')
        alert('Password changed successfully!')
      } else {
        throw new Error(result.message || 'Failed to change password')
      }
    } catch (error) {
      console.error('❌ Failed to change password:', error)
      alert(error.message || 'Failed to change password. Please try again.')
    } finally {
      setChangingPassword(false)
    }
  }

  const handlePasswordInputChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }))
  }

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }))
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>Loading profile...</Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    )
  }

  return (
    <Box>
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' }, 
          mb: 4,
          gap: 2
        }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              {t('profile.title')}
            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {t('profile.manageProfile')}
                </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            {!isEditing ? (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleEdit}
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                {t('common.edit')}
              </Button>
            ) : (
              <>
                <Button
                  variant="contained"
                  startIcon={saving ? null : <SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                  color="success"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {saving ? 'Saving...' : (t('common.save') || 'Save')}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {t('common.cancel')}
                </Button>
              </>
            )}
          </Box>
        </Box>
      </motion.div>

      <Grid container spacing={3}>
        {/* Profile Information */}
        <Grid item xs={12} md={8}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Avatar
                    sx={{ width: 80, height: 80, fontSize: '2rem' }}
                  >
                    {profile.name ? profile.name.charAt(0).toUpperCase() : 'A'}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={500}>
                      {profile.name || 'Admin User'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {profile.email}
                    </Typography>
                    <Chip
                      label={t('profile.administrator') || 'Administrator'}
                      color="primary"
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                  {isEditing && (
                                         <Tooltip title={t('profile.changePhoto')}>
                       <IconButton color="primary">
                         <CameraIcon />
                       </IconButton>
                     </Tooltip>
                  )}
                </Box>

                <Divider sx={{ my: 3 }} />

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('profile.name') || 'Full Name'}
                      value={isEditing ? tempProfile.name : profile.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      disabled={!isEditing}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('auth.email') || 'Email'}
                      value={isEditing ? tempProfile.email : profile.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      disabled={!isEditing}
                      margin="normal"
                      type="email"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('profile.role') || 'Role'}
                      value={profile.role}
                      disabled={true}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('Member Since') || 'Member Since'}
                      value={profile.createdAt ? new Date(profile.createdAt).toLocaleDateString('en-GB', { 
                        day: 'numeric', 
                        month: 'short', 
                        year: 'numeric' 
                      }) : ''}
                      disabled={true}
                      margin="normal"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Password Change Section */}
        <Grid item xs={12} md={4}>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {/* Change Password */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LockIcon color="primary" />
                  {t('Change Password') || 'Change Password'}
                </Typography>
                
                {!showPasswordForm ? (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {t('Update your account password for better security') || 'Update your account password for better security'}
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<LockIcon />}
                      onClick={() => setShowPasswordForm(true)}
                      fullWidth
                    >
                      {t('profile.changePassword') || 'Change Password'}
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    <TextField
                      fullWidth
                      label={t('profile.currentPassword') || 'Current Password'}
                      type={showPasswords.current ? 'text' : 'password'}
                      value={passwordData.currentPassword}
                      onChange={(e) => handlePasswordInputChange('currentPassword', e.target.value)}
                      margin="normal"
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            onClick={() => togglePasswordVisibility('current')}
                            edge="end"
                          >
                            {showPasswords.current ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        )
                      }}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('profile.newPassword') || 'New Password'}
                      type={showPasswords.new ? 'text' : 'password'}
                      value={passwordData.newPassword}
                      onChange={(e) => handlePasswordInputChange('newPassword', e.target.value)}
                      margin="normal"
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            onClick={() => togglePasswordVisibility('new')}
                            edge="end"
                          >
                            {showPasswords.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        )
                      }}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('profile.confirmPassword') || 'Confirm New Password'}
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={passwordData.confirmPassword}
                      onChange={(e) => handlePasswordInputChange('confirmPassword', e.target.value)}
                      margin="normal"
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            onClick={() => togglePasswordVisibility('confirm')}
                            edge="end"
                          >
                            {showPasswords.confirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        )
                      }}
                    />
                    
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <Button
                        variant="contained"
                        onClick={handlePasswordChange}
                        disabled={changingPassword}
                        startIcon={changingPassword ? null : <LockIcon />}
                        fullWidth
                      >
                        {changingPassword ? 'Changing...' : (t('Update Password') || 'Update Password')}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setShowPasswordForm(false)
                          setPasswordData({
                            currentPassword: '',
                            newPassword: '',
                            confirmPassword: ''
                          })
                        }}
                        disabled={changingPassword}
                      >
                        {t('common.cancel') || 'Cancel'}
                      </Button>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>
    </Box>
  )
}

export default Profile
